import { type RefObject } from 'react';

import type { RenderedLayout } from './DetailsLayoutContext.js';
import type { ExtractedDetailsSlot } from './slots.js';
import { useControllableState } from '../../../core/hooks/useControllableState.js';
import { useBreakpointCollapse } from '../shared/useBreakpointCollapse.js';

/** @internal */
export function useLayoutState({
  details,
  containerRef,
}: {
  details: ExtractedDetailsSlot;
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  const [preferredLayoutMode, setPreferredLayoutMode] = useControllableState({
    value: details.config.layout,
    defaultValue: details.config.defaultLayout,
    onChange: details.config.onLayoutChange,
  });

  const { containerWidth, collapsed, setCollapsed, isBelowBreakpoint } =
    useBreakpointCollapse({
      collapsed: {
        value: details.config.collapsed,
        defaultValue: details.config.defaultCollapsed ?? false,
        onChange: details.config.onCollapsedChange,
      },
      breakpoint: details.config.breakpoint,
      containerRef,
      onBreakpointTransition: details.config.onBreakpointTransition,
      autoCollapse: false,
    });

  let mode: RenderedLayout = preferredLayoutMode ?? 'split';
  if (collapsed) {
    mode = 'collapsed';
  }
  if (isBelowBreakpoint) {
    mode = 'drawer';
  }

  return {
    containerWidth,
    collapsed,
    setCollapsed,
    preferredLayoutMode,
    setPreferredLayoutMode,
    mode,
  };
}
